<template>
	<div class="welcome-container">
		<h1 class="welcome-title squids-font" v-if="phase === 'title'">
			{{ displayText }}<span class="cursor">_</span>
		</h1>
		<div class="quote-display" v-else-if="phase === 'quote' && currentQuote">
			<div class="quote-text">"{{ displayText }}"<span class="cursor">_</span></div>
			<div class="quote-author" v-if="authorVisible">— {{ currentQuote.author }}</div>
		</div>
		<div class="quote-display" v-else>
			<span class="cursor">_</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useQuoteStore } from '../stores/quote';
import type { Quote } from '../models/quote';

const props = defineProps<{
	title: string;
}>();

const quoteStore = useQuoteStore();

const phase = ref<'title' | 'erasing' | 'pause' | 'quote'>('title');
const displayText = ref('');
const currentQuote = ref<Quote | null>(null);
const authorVisible = ref(false);
let timer: ReturnType<typeof setTimeout> | null = null;
let typeInterval: ReturnType<typeof setInterval> | null = null;
let quoteIndex = 0;
let shuffledQuotes: Quote[] = [];

const CHAR_SPEED = 40;       // ms per character typing
const PAUSE_BETWEEN = 1500;  // pause between glitch and next quote
const QUOTE_DISPLAY = 10000; // 10 seconds per quote
const IDLE_DELAY = 30000;    // 30 seconds before quotes start

// Shuffle array (Fisher-Yates)
const shuffle = <T>(arr: T[]): T[] => {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
};

const typeText = (text: string, speed: number): Promise<void> => {
	return new Promise((resolve) => {
		let i = 0;
		displayText.value = '';
		typeInterval = setInterval(() => {
			if (i < text.length) {
				displayText.value += text[i];
				i++;
			} else {
				if (typeInterval) clearInterval(typeInterval);
				resolve();
			}
		}, speed);
	});
};

const GLITCH_CHARS = '!@#$%^&*()_+-=[]{}|;:,.<>?/~`01';
const GLITCH_STEPS = 6;
const GLITCH_STEP_MS = 80;
const GLITCH_FADE_MS = 40;

const glitchOut = (): Promise<void> => {
	return new Promise((resolve) => {
		const original = displayText.value;
		let step = 0;

		// Phase 1: Scramble text with increasing corruption
		typeInterval = setInterval(() => {
			if (step < GLITCH_STEPS) {
				const corruption = (step + 1) / GLITCH_STEPS;
				displayText.value = original
					.split('')
					.map(ch => Math.random() < corruption
						? GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)]
						: ch
					)
					.join('');
				step++;
			} else {
				// Phase 2: Rapidly shrink the garbled text
				if (typeInterval) clearInterval(typeInterval);
				typeInterval = setInterval(() => {
					if (displayText.value.length > 0) {
						// Remove 2-4 chars at a time for speed
						const remove = Math.min(2 + Math.floor(Math.random() * 3), displayText.value.length);
						displayText.value = displayText.value.slice(0, -remove);
					} else {
						if (typeInterval) clearInterval(typeInterval);
						resolve();
					}
				}, GLITCH_FADE_MS);
			}
		}, GLITCH_STEP_MS);
	});
};

const startQuoteCycle = async () => {
	if (quoteStore.quotes.length === 0) return;

	// Shuffle on first run or when exhausted
	if (shuffledQuotes.length === 0) {
		shuffledQuotes = shuffle(quoteStore.quotes);
		quoteIndex = 0;
	}

	// Glitch out title
	phase.value = 'erasing';
	await glitchOut();

	// Cycle quotes forever
	while (true) {
		phase.value = 'pause';
		await new Promise(r => timer = setTimeout(r, PAUSE_BETWEEN));

		// Pick next quote
		currentQuote.value = shuffledQuotes[quoteIndex % shuffledQuotes.length];
		quoteIndex++;
		if (quoteIndex >= shuffledQuotes.length) {
			shuffledQuotes = shuffle(quoteStore.quotes);
			quoteIndex = 0;
		}

		// Type quote
		phase.value = 'quote';
		authorVisible.value = false;
		await typeText(currentQuote.value.text, CHAR_SPEED);

		// Show author
		authorVisible.value = true;

		// Wait
		await new Promise(r => timer = setTimeout(r, QUOTE_DISPLAY));

		// Glitch out quote
		authorVisible.value = false;
		await glitchOut();
	}
};

onMounted(async () => {
	// Show title immediately
	displayText.value = props.title;

	// Fetch quotes in background (single read)
	await quoteStore.fetchAll();

	// After 30 seconds, start the quote cycle
	if (quoteStore.quotes.length > 0) {
		timer = setTimeout(() => {
			startQuoteCycle();
		}, IDLE_DELAY);
	}
});

onUnmounted(() => {
	if (timer) clearTimeout(timer);
	if (typeInterval) clearInterval(typeInterval);
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');

.welcome-container {
	text-align: center;
	min-height: 120px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.welcome-title {
	font-size: 5rem;
	color: #00ffea;
	letter-spacing: 4px;
	margin: 0;
}

.cursor {
	color: #ff6600;
	animation: blink 1s step-end infinite;
}

@keyframes blink {
	0%, 100% { opacity: 1; }
	50% { opacity: 0; }
}

.quote-display {
	max-width: 700px;
	min-height: 80px;
}

.quote-text {
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 1.3em;
	color: #00ffea;
	letter-spacing: 1px;
	line-height: 1.6;
	font-style: italic;
}

.quote-author {
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 0.85em;
	color: #ff6600;
	margin-top: 12px;
	letter-spacing: 2px;
	animation: fade-in 0.5s ease-in;
}

@keyframes fade-in {
	from { opacity: 0; }
	to { opacity: 1; }
}
</style>
